/**
 * One-shot toast handoff across a full page navigation.
 *
 * The storefront wizard embed finishes an apply and then LEAVES the page
 * (`window.location.assign` into the app), so a toast pushed there would be
 * gone before anyone read it. Instead the embed stashes the toast here and the
 * SPA drains it on boot (`<PendingToast>` in App.tsx) — same origin, same tab,
 * so sessionStorage is the natural carrier (survives the navigation, dies with
 * the tab, never leaks to another tab).
 *
 * A short TTL keeps a stale entry (tab restored days later) from surfacing a
 * toast about something the athlete no longer remembers doing.
 */

export type PendingToast = {
  tone: "info" | "success" | "warning" | "error";
  message: string;
  detail?: string;
  durationMs?: number;
};

export const PENDING_TOAST_KEY = "fg.pendingToast";

/** How long a stashed toast stays deliverable — 2 minutes. */
export const PENDING_TOAST_TTL_MS = 2 * 60 * 1000;

type Stored = PendingToast & { at: number };

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const defaultStorage = (): StorageLike | null =>
  typeof sessionStorage !== "undefined" ? sessionStorage : null;

/** Stash a toast for the next SPA boot in this tab (best-effort). */
export function stashPendingToast(
  toast: PendingToast,
  storage: StorageLike | null | undefined = defaultStorage(),
  now: number = Date.now()
): void {
  if (!storage) return;
  try {
    const stored: Stored = { ...toast, at: now };
    storage.setItem(PENDING_TOAST_KEY, JSON.stringify(stored));
  } catch {
    /* quota / private mode — the toast is a convenience, not state */
  }
}

/**
 * Read AND clear the stashed toast. `null` when absent, malformed, or older
 * than the TTL (a stale entry is cleared on read too).
 */
export function consumePendingToast(
  storage: StorageLike | null | undefined = defaultStorage(),
  now: number = Date.now()
): PendingToast | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(PENDING_TOAST_KEY);
    if (!raw) return null;
    storage.removeItem(PENDING_TOAST_KEY);
    const parsed = JSON.parse(raw) as Partial<Stored> | null;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.message !== "string" || !parsed.message) return null;
    if (typeof parsed.at !== "number" || now - parsed.at > PENDING_TOAST_TTL_MS) return null;
    const tone =
      parsed.tone === "info" || parsed.tone === "success" || parsed.tone === "warning" || parsed.tone === "error"
        ? parsed.tone
        : "info";
    return {
      tone,
      message: parsed.message,
      ...(typeof parsed.detail === "string" ? { detail: parsed.detail } : {}),
      ...(typeof parsed.durationMs === "number" ? { durationMs: parsed.durationMs } : {})
    };
  } catch {
    return null;
  }
}
